import type { QuestionStatus } from '@titularis/api-client';

/**
 * Quando o botão "Publicar" tem o que fazer.
 *
 * Mora fora da tela porque é regra, e não desenho: ela decide o que o aluno
 * passa a ver, e é a mesma conta que a API refaz antes de gravar. Repetida
 * dentro do JSX, seria uma expressão de três condições no meio do cabeçalho -
 * onde ninguém a lê e ninguém a testa.
 */

type EstadoDaQuestao = {
  status: QuestionStatus;
  /** A versão que está no ar. Nula enquanto a questão nunca foi publicada. */
  currentVersionId: string | null;
  /** A última versão salva, publicada ou não. */
  latestVersionId: string | null;
};

/**
 * Há o que publicar quando a última versão salva não está no ar: porque é nova,
 * ou porque a questão saiu do ar com a versão vigente intacta - voltou para
 * rascunho, ou teve a anulação desfeita.
 *
 * Comparar só as duas versões deixava o botão morto justamente depois de tirar
 * a questão do ar: a vigente continua sendo a última, e o "republicar é um
 * clique" que o domínio promete exigia passar pela lista de questões.
 *
 * **A anulada também se publica** (D-61): vai ao ar marcada, sem gabarito. Nela,
 * estar no ar é ter versão vigente, então há o que publicar sempre que a
 * vigente não é a última salva - inclusive quando não há vigente nenhuma.
 */
export function haVersaoParaPublicar(questao: EstadoDaQuestao | undefined): boolean {
  if (!questao?.latestVersionId) return false;
  if (questao.status === 'annulled') return questao.latestVersionId !== questao.currentVersionId;

  return questao.status !== 'published' || questao.latestVersionId !== questao.currentVersionId;
}

/**
 * A anulada que o aluno já vê (D-61).
 *
 * O status é o mesmo da anulada guardada; o que muda é ter versão vigente, que
 * na anulada quer dizer estar no ar. É o que acende "Tirar do ar" e muda a
 * etiqueta para "Anulada · no ar".
 */
export function anuladaNoAr(questao: EstadoDaQuestao | undefined): boolean {
  return questao?.status === 'annulled' && Boolean(questao.currentVersionId);
}
